import React from "react";
import dayjs from "@/config/dayjs";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import { Button, Form, FormField, TextInput } from "../ui";
import type { UserCustomer, UserData } from "../../types/state";

type CustomerExtended = UserCustomer & { email?: string; company_name?: string };

interface ProfileFormOwnProps extends WithTranslation {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  formData: Record<string, string>;
  formError: Record<string, { code: string; message: string }>;
  userData: UserData & {
    parent_user?: unknown;
    username?: string;
    last_name?: string;
    first_name?: string;
    last_connection_at?: string;
    created_at?: string;
    role?: string;
  };
  customerData: CustomerExtended;
  message?: unknown;
  readonly?: boolean;
}

function renderDefinition(label: React.ReactNode, value: React.ReactNode, key: string) {
  return (
    <div className="ComposedForm-block" key={key}>
      <dt className="ComposedForm-label">{label}</dt>
      <dd className="ComposedForm-line">{value}</dd>
    </div>
  );
}

function ProfileForm({
  t,
  i18n,
  onChange,
  onSubmit,
  formData,
  formError,
  userData,
  customerData,
  readonly,
}: ProfileFormOwnProps) {
  const formatDate = (date?: string | number | Date | null) => {
    if (!date) {
      return t("common.label_nc");
    }
    const locale = i18n?.resolvedLanguage || i18n?.language || "fr";
    return dayjs(date).locale(locale).format("L LT");
  };

  const isDelegateUser = Boolean(userData.parent_user);
  const email = isDelegateUser ? userData.username : customerData.email;

  return (
    <Form action="/" onSubmit={onSubmit} className="ComposedForm">
      <fieldset className="ComposedForm-fieldset">
        <legend className="ComposedForm-legend">{t("profile.profil.personal_info")}</legend>
        <dl className="ComposedForm-fieldsetInner">
          {renderDefinition(t("profile.profil.identification"), customerData.id, "identification")}
          {isDelegateUser ? (
            <>
              <FormField
                className="ComposedForm-block"
                label={t("profile.profil.name")}
                htmlFor="ProfileForm-lastName"
                error={formError.last_name?.message}
              >
                <TextInput
                  className="ComposedForm-input"
                  name="last_name"
                  id="ProfileForm-lastName"
                  maxLength={255}
                  value={formData.last_name || ""}
                  onChange={onChange}
                  readOnly={!!readonly}
                />
              </FormField>
              <FormField
                className="ComposedForm-block"
                label={t("profile.profil.firstname")}
                htmlFor="ProfileForm-firstName"
                error={formError.first_name?.message}
              >
                <TextInput
                  className="ComposedForm-input"
                  name="first_name"
                  id="ProfileForm-firstName"
                  maxLength={255}
                  value={formData.first_name || ""}
                  onChange={onChange}
                  readOnly={!!readonly}
                />
              </FormField>
            </>
          ) : (
            <>
              {renderDefinition(t("profile.profil.name"), userData.last_name, "last_name")}
              {renderDefinition(t("profile.profil.firstname"), userData.first_name, "first_name")}
            </>
          )}
          {renderDefinition(t("profile.profil.email"), email, "email")}
          {renderDefinition(
            t("profile.profil.last_connection"),
            formatDate(userData.last_connection_at),
            "last_connection_at"
          )}
          {renderDefinition(
            t("profile.profil.account_created_at"),
            formatDate(userData.created_at),
            "created_at"
          )}
          {renderDefinition(
            t("profile.profil.permissions"),
            t(`profile.roles.${userData.role || "unknown"}`),
            "role"
          )}
        </dl>
      </fieldset>
      <fieldset className="ComposedForm-fieldset">
        <legend className="ComposedForm-legend">{t("profile.profil.customer_info")}</legend>
        <dl className="ComposedForm-fieldsetInner">
          {renderDefinition(t("profile.profil.company"), customerData.company_name, "company_name")}
        </dl>
      </fieldset>
      {isDelegateUser && !readonly ? (
        <div className="ComposedForm-block isActionLine">
          <Button type="submit" size="lg" className="Button ComposedForm-submit isLarge">
            {t("common.button_register")}
          </Button>
        </div>
      ) : null}
    </Form>
  );
}

export default compose(withTranslation())(ProfileForm);
